import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteGmailAccountDelegate,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { DelegateModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteAccountDelegateProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDelegate: DelegateModel;
  delegateList: DelegateModel[] | undefined;
  changeLogRoles: string[];
}

export default function DeleteAccountDelegateDialog({
  open,
  handleClose,
  userEmail,
  selectedDelegate,
  delegateList,
  changeLogRoles,
}: DeleteAccountDelegateProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteGmailAccountDelegate(
        userEmail,
        selectedDelegate.delegateEmail,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Account delegate'),
      });

      const newList = delegateList
        ? delegateList?.filter(
            (delegate) =>
              delegate.delegateEmail !== selectedDelegate.delegateEmail
          )
        : [];

      queryClient.setQueryData([UserKeys.AccountDelegates, userEmail], newList);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected account delegate'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_account_delegate,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.accountDelegation,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.RemoveGmailAccountDelegation.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Remove Account Delegate"
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            Remove <strong>{selectedDelegate.delegateEmail}</strong> as an
            account delegate to <strong>{userEmail}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Remove Delegate',
        action: handleSubmit,
        loading: isPending,
        disabled: false,
        color: 'error',
      }}
    />
  );
}
